import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef} from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from "recharts";
import {observer} from "mobx-react";
import {DataPoint, formatDateForInput, rateOfReturn, useChartStore} from "../stores/chartStore";
import {isMobile} from "react-device-detect";
import clsx from "clsx";
import {bisectLeft} from "d3-array";

const percentage = (x: number | undefined) => {
  let xx = x === undefined ? NaN : x
  const prefix = xx >= 0 ? '+' : ''
  return `${prefix}${xx.toLocaleString(undefined, {style: 'percent', minimumFractionDigits: 2})}`
}

export const Chart = observer(function Chart() {
  const chartStore = useChartStore()

  const {
    leftZoomScaled,
    rightZoomScaled,
    leftZoomedScaledTimestamp,
    rightZoomedScaledTimestamp,
    leftZoomPreviewTimestamp,
    rightZoomPreviewTimestamp,
    leftZoomScaledTimestamp,
    rightZoomScaledTimestamp,
    leftZoomDateInput,
    rightZoomDateInput,
    firstDate,
    lastDate,
    leftZoomDataPoint,
    selectedDataColumn,
    setSelectedDataColumn,
    valueLabels,
    dataScaled,
    zoomedDataScaled,
    zoomShiftInProgress,
    canShowZoomPreviewInMainChart,
    canShowZoomPreviewInMiniMap,
    doShowZoomedRegionInMiniMap,
    dataUrl,
    error,
    data,
    leftZoom,
    rightZoom,
    zoomedDataRateOfReturn,
    mainChartHandleMouseDown,
    mainChartHandleMouseMove,
    mainChartHandleMouseUp,
    miniMapHandleMouseDown,
    miniMapHandleMouseMove,
    miniMapHandleMouseUp,
    zoom,
    handleChangeLeftZoomDate,
    handleChangeRightZoomDate,
    valueGetter,
    setWidth,
    init,
  } = chartStore

  useEffect(init, [chartStore])

  const ref = useRef<AreaChart>(null)
  useLayoutEffect(() => {
    const {width, margin: {left = 0, right = 0} = {}} = ref.current?.props || {}
    if (width !== undefined) {
      setWidth(width - left - right)
    }
  })

  const buttons: Array<[string, number, number, boolean]> = useMemo(() => firstDate && lastDate ? ([
    ['ALL', firstDate],
    ['3 YEARS', new Date(lastDate.getFullYear() - 3, lastDate.getMonth(), lastDate.getDate())],
    ['1 YEAR', new Date(lastDate.getFullYear() - 1, lastDate.getMonth(), lastDate.getDate())],
    ['YTD', new Date(lastDate.getFullYear(), 0, 0)],
  ] as [string, Date][])
    .filter(([, date]) => date && date >= firstDate)
    .map(([title, fromDate]) => {
      const start = bisectLeft(chartStore.dates, fromDate)
      const end = data.length - 1

      return [
        title,
        start,
        end,
        leftZoom === start && rightZoom === end,
      ]
    }) : [], [chartStore, data.length, firstDate, lastDate, leftZoom, rightZoom])

  const tooltipValueFormatterInMainChart: TooltipProps['formatter'] = useCallback(v => (
    <div>
      <div>{percentage(rateOfReturn(valueGetter(leftZoomDataPoint), v as number))}</div>
      <div>{v.toLocaleString()}</div>
    </div>
  ), [leftZoomDataPoint, valueGetter])

  const tooltipValueFormatterInMiniMap: TooltipProps['formatter'] = useCallback(v => (
    <div>
      <div>{percentage(rateOfReturn(valueGetter(data[0]), v as number))}</div>
      <div>{v.toLocaleString()}</div>
    </div>
  ), [data, valueGetter])

  if (error) {
    return (
      <div>
        <p>Failed to load or parse chart data at "{dataUrl}": {error}</p>
        <p>Possible solutions:</p>
        <ul>
          <li>If chart data has wrong format (e.g. not csv) check and update the file according to the format.</li>
          <li>If file is missing, then either remove it from settings or try to upload it again.</li>
        </ul>
      </div>
    )
  }

  return (
    <div className='wem-chart-chart-root' style={{minHeight: isMobile ? 468 : 658}}>
      <div className='wem-chart-controls-container'>
        <div className='d-flex justify-content-center flex-wrap flex-grow-1'>
          <div className='wem-chart-currency-buttons'>
            {valueLabels.map((label, i) => {
              const current = i === selectedDataColumn
              return (
                <button
                  key={label}
                  className={clsx('wem-chart-button', current && 'current')}
                  disabled={current}
                  onClick={() => setSelectedDataColumn(i)}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className='wem-chart-rate-of-return'>
            {percentage(zoomedDataRateOfReturn)}
          </div>
        </div>
        <div className='d-flex justify-content-center flex-wrap flex-grow-1'>
          <div className='wem-chart-range-buttons'>
            {buttons.map(([title, start, end, current]) => (
              <button
                key={title}
                className={clsx('wem-chart-button', current && 'current')}
                disabled={current}
                onClick={() => zoom(start, end)}
              >
                {title}
              </button>
            ))}
          </div>
          <div className='wem-chart-range-inputs'>
            <input
              type='date'
              value={formatDateForInput(leftZoomDateInput)}
              min={formatDateForInput(firstDate)}
              max={formatDateForInput(rightZoomDateInput)}
              onChange={handleChangeLeftZoomDate}
            />
            <input
              type='date'
              value={formatDateForInput(rightZoomDateInput)}
              min={formatDateForInput(leftZoomDateInput)}
              max={formatDateForInput(lastDate)}
              onChange={handleChangeRightZoomDate}
            />
          </div>
        </div>
      </div>

      <ResponsiveContainer minHeight={isMobile ? 250 : 400} width="100%" height="100%">
        <AreaChart
          ref={ref}
          className="wem-chart-chart"
          data={zoomedDataScaled}
          margin={{top: 10, right: 20, left: 0, bottom: 0}}
          stackOffset="expand"
          syncId="mainChart"
          onMouseDown={mainChartHandleMouseDown}
          onMouseMove={mainChartHandleMouseMove}
          onMouseUp={mainChartHandleMouseUp}
        >
          <CartesianGrid strokeDasharray="2 6"/>
          <XAxis
            allowDataOverflow
            dataKey={({date}: DataPoint) => date.getTime()}
            tickFormatter={(time: number) => new Date(time).toLocaleDateString()}
            domain={[leftZoomScaled, rightZoomScaled]}
            type="category"
            stroke="#808080"
          />
          <YAxis
            allowDataOverflow
            dataKey={valueGetter}
            domain={['auto', 'auto']}
            type="number"
            stroke="#808080"
            tickFormatter={v => v.toLocaleString()}
          />

          <Tooltip
            contentStyle={{background: '#000000a0', color: '#fff'}}
            itemStyle={{color: '#fff'}}
            active={!zoomShiftInProgress}
            formatter={tooltipValueFormatterInMainChart}
            labelFormatter={time => new Date(time).toLocaleDateString()}
          />

          <defs>
            <linearGradient id="fillGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#808080" stopOpacity={0.9}/>
              <stop offset="95%" stopColor="#808080" stopOpacity={0.2}/>
            </linearGradient>
          </defs>

          <Area
            type="monotone"
            dataKey={valueGetter}
            stroke="#f5f5f5"
            fill="url(#fillGradient)"
            animationDuration={300}
            isAnimationActive={!zoomShiftInProgress}
          />

          {canShowZoomPreviewInMainChart ? (
            <ReferenceArea
              x1={leftZoomedScaledTimestamp}
              x2={rightZoomedScaledTimestamp}
              strokeOpacity={0.3}
              fillOpacity={0.2}
              ifOverflow="hidden"
              stroke="#fff"
              fill="orange"
            />
          ) : null}

        </AreaChart>
      </ResponsiveContainer>

      <div style={{margin: 16}}/>

      <ResponsiveContainer minHeight={isMobile ? 120 : 160} width="100%" height="100%">
        <AreaChart
          className="wem-chart-chart"
          data={dataScaled}
          margin={{top: 10, right: 20, left: 0, bottom: 0}}
          stackOffset="expand"
          syncId="miniMapChart"
          onMouseDown={miniMapHandleMouseDown}
          onMouseMove={miniMapHandleMouseMove}
          onMouseUp={miniMapHandleMouseUp}
        >
          <CartesianGrid strokeDasharray="2 6"/>
          <XAxis
            allowDataOverflow
            dataKey={({date}: DataPoint) => date.getTime()}
            tickFormatter={(time: number) => new Date(time).toLocaleDateString()}
            type="category"
            stroke="#808080"
          />
          <YAxis
            dataKey={valueGetter}
            allowDataOverflow
            domain={['auto', 'auto']}
            type="number"
            stroke="#808080"
            tickFormatter={v => v.toLocaleString()}
          />
          <defs>
            <linearGradient id="fillGradient2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#808080" stopOpacity={0.9}/>
              <stop offset="95%" stopColor="#808080" stopOpacity={0.2}/>
            </linearGradient>
          </defs>

          <Tooltip
            wrapperStyle={{opacity: isMobile ? 0 : 1}}
            contentStyle={{background: '#000000a0', color: '#fff'}}
            itemStyle={{color: '#fff'}}
            active={!zoomShiftInProgress}
            isAnimationActive={!zoomShiftInProgress}
            formatter={tooltipValueFormatterInMiniMap}
            labelFormatter={time => new Date(time).toLocaleDateString()}
          />

          <Area
            type="monotone"
            dataKey={valueGetter}
            stroke="#f5f5f5"
            fill="url(#fillGradient2)"
            animationDuration={300}
          />

          {canShowZoomPreviewInMiniMap ? (
            <ReferenceArea
              x1={leftZoomPreviewTimestamp}
              x2={rightZoomPreviewTimestamp}
              strokeOpacity={0.3}
              fillOpacity={0.2}
              ifOverflow="hidden"
              stroke="#fff"
              fill="orange"
            />
          ) : null}

          {doShowZoomedRegionInMiniMap ? (
            <ReferenceArea
              x1={leftZoomScaledTimestamp}
              x2={rightZoomScaledTimestamp}
              strokeOpacity={0.3}
              fillOpacity={zoomShiftInProgress ? 0.5 : 0.2}
              ifOverflow="hidden"
              stroke="#fff"
              fill="orange"
            />
          ) : null}

        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
})
